import { toTitleCase, buildPhotoUrl } from '@/shared/utils/mapping';
import type { Timelog, TimelogEmployee } from '../../domain/entities/Timelog';
import type { TimelogDraft } from '../../domain/entities/TimelogDraft';
import type { TimelogDTO, CreateTimelogRequestDTO, EmployeeLookupDTO } from '../dto/TimelogDTO';
import { TIMELOG_SOURCE } from '../../domain/constants/verificationMode';

const AVATAR_COLORS = [
  '#2485F4', '#28C76F', '#EA5455', '#53DBD4',
  '#FF9F43', '#7367F0', '#00CFE8', '#FF6B6B',
];

function colorForPerson(idPerson: number): string {
  return AVATAR_COLORS[idPerson % AVATAR_COLORS.length]!;
}

function initials(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

export function toTimelogEmployee(dto: TimelogDTO['employee']): TimelogEmployee {
  const firstName = toTitleCase(dto.first_name);
  const lastName  = toTitleCase(dto.last_name1);
  return {
    idPerson:    dto.id_person,
    code:        dto.code,
    firstName,
    lastName,
    fullName:    `${firstName} ${lastName}`,
    initials:    initials(firstName, lastName),
    avatarColor: colorForPerson(dto.id_person),
    department:  dto.entity_type,
    photoUrl:    buildPhotoUrl(dto.photo),
  };
}

export function toTimelog(dto: TimelogDTO): Timelog {
  const isObjectPayload = !Array.isArray(dto.payload_timelog) && dto.payload_timelog != null;
  const payload = isObjectPayload ? (dto.payload_timelog as Record<string, unknown>) : {};
  // datetime_pg is the LOCAL time to display (server's configured timezone).
  // dto.timelog is UTC and must NOT be used for display.

  // Defensively extract source: only when payload is an object and source is a string.
  const rawSource = payload['source'];
  const source: string | null = typeof rawSource === 'string' ? rawSource : null;

  // Defensively extract photo: only when payload is an object and photo is a string.
  const rawPhoto = payload['photo'];
  const photo: string | null = typeof rawPhoto === 'string' ? rawPhoto : null;

  // Defensively extract location: only when payload is an object and location is a string.
  const rawLocation = payload['location'];
  const location: string | null = typeof rawLocation === 'string' ? rawLocation : null;

  return {
    idTimelog:          dto.id_timelog,
    idPerson:           dto.employee.id_person,
    idEntity:           dto.id_entity,
    idDevice:           dto.id_device,
    datetime:           dto.datetime_pg,
    accessType:         dto.access_type_timelog,
    verificationMode:   dto.id_verification_mode,
    verificationResult: dto.verification_result_timelog === 1,
    isOffline:          payload['offline'] === 1,
    coordinates:        dto.coordinates
      ? { lat: parseFloat(dto.coordinates.lat), lng: parseFloat(dto.coordinates.lng) }
      : null,
    employee:           toTimelogEmployee(dto.employee),
    description:        dto.description ?? null,
    deviceActionType:   dto.id_device_action_type ?? null,
    structureCode:      dto.structure?.code ?? null,
    capturePhotoUrl:    buildPhotoUrl(photo),
    source,
    location,
    // Slice C1: remote-sourced rows are by definition synced; localId is reserved for drafts.
    syncStatus:         'synced',
    localId:            null,
  };
}

export function toCreateTimelogRequest(draft: TimelogDraft): CreateTimelogRequestDTO {
  return {
    access_type_timelog: draft.accessType,
    id_verification_mode: draft.verificationMode,
    id_structure: draft.idStructure,
    id_entity: draft.idEntity,
    timezone_pg: draft.timezone,
    payload_timelog: {
      offline: draft.isOffline ? 1 : 0,
      id_biometric: draft.idBiometric,
      serial_device: draft.serialDevice ?? '',
      source: TIMELOG_SOURCE.clockWeb,
      location: draft.location,
    },
    coordinates: draft.coordinates,
    timelog: draft.datetime,
    // Omit photo entirely when null/undefined — backend rejects the key for non-facial flows (e.g. PIN)
    ...(draft.photo != null ? { photo: draft.photo } : {}),
    // Convert string device ID to number for the backend DTO boundary.
    // Device IDs stored as strings in TimelogDraft; backend expects integer or null.
    id_device: draft.idDevice !== null ? (Number(draft.idDevice) || null) : null,
  };
}

const TEMPLATE_PIN = 5;

export interface AttendanceEmployee {
  idPerson: number;
  idEntity: number;
  code: string;
  fullName: string;
  initials: string;
  avatarColor: string;
  dui: string;
  department: string;
  pinBiometricId: number | null;
  pinCode: string | null;
  /** Organizational structure name (e.g. branch/site). Undefined when backend omits it. */
  structureName?: string;
  /** Photo URL built from Employee.photoUrl. Undefined when source DTO lacks photo field. */
  photoUrl?: string | null;
}

export function toAttendanceEmployee(dto: EmployeeLookupDTO): AttendanceEmployee {
  const firstName = toTitleCase(dto.first_name);
  const lastName  = toTitleCase(dto.last_name1);

  const pinBiometric = dto.biometric_data?.find(
    (b) => b.id_template_biometric === TEMPLATE_PIN,
  ) ?? null;
  const pinCode = pinBiometric && Array.isArray(pinBiometric.biometric_data)
    ? (pinBiometric.biometric_data as number[]).join('')
    : null;

  return {
    idPerson:       dto.id_person,
    idEntity:       dto.id_entity,
    code:           dto.code,
    fullName:       `${firstName} ${lastName}`,
    initials:       initials(firstName, lastName),
    avatarColor:    colorForPerson(dto.id_person),
    dui:            dto.dui_person,
    department:     dto.entity_type,
    pinBiometricId: pinBiometric?.nro_biometric ?? null,
    pinCode,
    ...(dto.structure_name != null ? { structureName: dto.structure_name } : {}),
    // photoUrl will be enriched by CompositeEmployeeLookupRepository (Phase 6 Item 5)
  };
}
